import { createContext, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  type GovernedSettingBucketView,
  type GovernedSettingEffectiveResponse,
  type GovernedSettingKeyResponse,
  type GovernedSettingSummaryView,
  type GovernedSettingVersionView,
  type PolicySettingView,
  type SettingChangeView,
} from '@vello/api-client';
import { type SettingField, settingFormIssues } from '@vello/setting-forms/form';
import {
  bucketAt,
  bucketForLevel,
  callingCodeDraft,
  canRevertTo,
  chainRows,
  effectiveQueryOf,
  formatMarketInstant,
  initialEditorValue,
  isLevel,
  levelFromQuery,
  levelId,
  levelsOfPlace,
  lifecycleTone,
  oneStepRollbackTarget,
  otherPlaces,
  otpAbuseFieldValue,
  placeOfLevel,
  ruleDomain,
  ruleList,
  rulePendingCount,
  ruleSource,
  scheduleToUtc,
  scheduledCollisions,
  settingDiffChanges,
  settingEditorFor,
  settingValueDiff,
  submitBlock,
  withOtpAbuseField,
  type ChainTag,
  type OperatorScope,
  type RuleFilter,
  type RuleLevel,
  type RuleRow,
  type SettingDiffRow,
} from '../../src/index';
import { PersonName } from '../components/person-name';
import { MoneySettingEditor } from '../components/money-setting-editor';
import { SettingFormEditor, useMoneyCurrency, useSettingValueView, type SettingValueView } from '../components/setting-form-editor';
import { FinalFareExample } from '../components/final-fare-example';
import { changedRows, rowCount, valueJson, type ValueRow } from '../components/setting-value';
import { CentralAiValueEditor } from '../components/central-ai-editors';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { parseQuery } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useScopeName } from '../i18n/scope-name';
import {
  Badge, Button, CapWidth, CheckRow, Cluster, EmptyState, EmptyValue, Field, FormRow, Grid, HairlineRow, HairlineRowMain, HistoryEntry, LevelChain, LevelChainItem,
  LevelNote, LevelSwitch, MetaLine, MonoText, Notice, OptionChip, OptionChips, PageHeader, Panel, PendingProposal, RuleCard, RuleColumn, RuleColumns, RuleDetail,
  RuleCardBody, RuleDescription, RuleDisclosure, RuleGroup, RuleListEmpty, RuleListPanel, RulePill, RuleRowButton, RuleSearch, RulesWorkspace, RuleTitle, SegmentedFilter, Select, Stack, SubmitRow, Text, TextArea,
  TextInput, ValueBlock, ValueFold, ValueList, type RulePillTone, type UiIconName,
} from '@vello/web-theme';

/**
 * **Configurações › Regras governadas** (W6-SETTINGS-CONSOLE, D400; ADR-0060), in the Claude Design layout of D762 (`Admin Web - Regras
 * Governadas.dc.html`, screen A12 "Regras governadas (redesign)") — the operator surface over `/v1/config/settings/*`, the HTTP face of the D393
 * governed-settings kernel.
 *
 * ONE PLACE, ONE LEVEL. The page follows the top scope selector (Plataforma inteira › País › Estado › Cidade) and the operator picks the LEVEL they
 * look at and edit in the switch beside the title. The rule list on the left says, for every rule, where its value at that level comes from (its own,
 * inherited from a level above, or nothing) and what waits for review; the selected rule on the right shows, in order: what waits for review, the
 * value in force at the level, the proposal, the value per level (the server's own fold, so a lock above shows why) and the history.
 *
 * FOUR EYES (owner decision D391 #5). Every change of a governed key needs a maker and a DIFFERENT checker. This screen is both sides:
 *  - a **typed editor per key** (a choice, a token set, a bounded duration, a form — never a free-text blob) and the **diff** against what governs;
 *  - a **required reason**, typed in the proposal itself (the reviewer reads it) and on every decision (a confirmation asks for it);
 *  - the **review queue** of the rule: who proposed what, where and why, with Rejeitar / Retirar / Aprovar e publicar;
 *  - **"Reverter para vN"**, one step back (applied at once, reviewed afterwards) or further back (four eyes);
 *  - **scheduling in the market's time zone** — the field is the market's wall clock, the wire is UTC.
 *
 * It never hides an action to enforce a rule: the server decides (`SELF_APPROVAL`, `NOT_REQUESTER`, `STALE_BASE`, `SCHEDULED_VERSION_COLLISION`)
 * and the refusal is surfaced in the operator's language. One primary action on the screen: "Enviar para revisão" (the design's navy "Aprovar e
 * publicar" stays a secondary button here — AGENTS.md, one primary action per screen).
 *
 * Review W6S-01 / W6G-02 / W6G-05 (scheduled versions a decision would cancel, named by the maker and by the checker), W6G-01 (an applied rollback's
 * post-review), W6G-04 (a version no reader can verify fails its bucket closed) and the read-only **policies viewer** (Admin checklist #59) are kept.
 */
export interface GovernedSettingsScreenProps {
  /** D631: show only these keys (an area of the console, such as "Atendimento e IA", reuses this whole flow for its own keys). */
  readonly only?: readonly string[];
  /** D631: replaces the page title, subtitle and explanation (message ids). */
  readonly heading?: { readonly title: string; readonly subtitle: string; readonly explain: string };
  /**
   * D631: the countries an area offers places at. Since D762 every level of the place on screen can receive a first version, so the screen no
   * longer needs it; it is accepted for the callers that pass it.
   */
  readonly countries?: readonly string[];
  /** D631: rendered above the workspace, with the loaded keys and a way to open a key at a place. */
  readonly renderAbove?: (ctx: { readonly summaries: readonly GovernedSettingSummaryView[]; readonly open: (key: string, scope: 'platform' | 'country', scopeRef: string) => void; readonly selectedKey: string | undefined }) => ReactNode;
  /** D631: leave out the read-only viewer of the generic policies. */
  readonly hidePolicies?: boolean;
  /**
   * D762: the design's `showKeys` — each rule's technical key under its name and beside the title (with a copy button). On by default here, where
   * operators search and quote keys; an area that prints no stored code (D577, "Atendimento e IA") turns it off.
   */
  readonly showKeys?: boolean;
}

const LEVEL_ICON: Readonly<Record<RuleLevel['scope'], UiIconName>> = { platform: 'globe', country: 'flag', region: 'map', market: 'mapPin' };

const renderValue = valueJson;
/** D775 / D854: the selected rule's value in words (through its form: rows and one line); the JSON text when the rule has no form. */
const ValueText = createContext<SettingValueView>({ text: renderValue, rows: () => undefined });

/** A value on a card: a definition list in the body font when the rule's form describes it, else the JSON text in the value box. */
function ValueBody({ value, size }: { value: unknown; size?: 'sm' }): ReactElement {
  const view = useContext(ValueText);
  const rows = view.rows(value);
  return rows === undefined ? <ValueBlock {...(size !== undefined ? { size } : {})}>{renderValue(value)}</ValueBlock> : <ValueList rows={rows} />;
}

/** A value kept short: a few fields show in place, a long policy waits behind "Ver valor". */
const INLINE_FIELDS = 4;
function ValueSummary({ value }: { value: unknown }): ReactElement {
  const { t } = useT();
  const view = useContext(ValueText);
  const rows = view.rows(value);
  if (rows === undefined) return <>{renderValue(value)}</>;
  if (rowCount(rows) <= INLINE_FIELDS) return <ValueList rows={rows} />;
  return <ValueFold label={t('govset.value.show')}><ValueList rows={rows} /></ValueFold>;
}

/** A proposal in the review queue: the fields it changes (old struck, new marked); the whole value when there is nothing to compare. */
function ValueProposed({ from, to, hasFrom }: { from: unknown; to: unknown; hasFrom: boolean }): ReactElement {
  const { t } = useT();
  const view = useContext(ValueText);
  const rows = view.rows(to);
  const prior = hasFrom ? view.rows(from) : undefined;
  if (rows === undefined) return <ValueBlock size="sm">{hasFrom ? `${renderValue(from)} → ${renderValue(to)}` : renderValue(to)}</ValueBlock>;
  if (prior === undefined) return <ValueList rows={rows} />;
  const changes = changedRows(prior, rows, t('govset.value.removed'));
  return (
    <>
      {changes.length === 0 ? <Text role="caption" tone="tertiary" block>{t('govset.value.same')}</Text> : <ValueList rows={changes} />}
      <ValueFold label={t('govset.value.full')} hideLabel={t('govset.value.hide')}><ValueList rows={rows} /></ValueFold>
    </>
  );
}

/** A history version: only the fields that differ from the version before it (the first version says so), the whole value one click away. */
function ValueChanges({ value, before }: { value: unknown; before: { readonly value: unknown } | undefined }): ReactElement {
  const { t } = useT();
  const view = useContext(ValueText);
  const rows = view.rows(value);
  if (rows === undefined) return <>{renderValue(value)}</>;
  const prior = before === undefined ? undefined : view.rows(before.value);
  const changes: readonly ValueRow[] | undefined = prior === undefined ? undefined : changedRows(prior, rows, t('govset.value.removed'));
  return (
    <>
      {changes === undefined
        ? <Text role="caption" tone="tertiary" block>{t('govset.value.first')}</Text>
        : changes.length === 0
          ? <Text role="caption" tone="tertiary" block>{t('govset.value.same')}</Text>
          : <ValueList rows={changes} />}
      <ValueFold label={t('govset.value.full')} hideLabel={t('govset.value.hide')}><ValueList rows={rows} /></ValueFold>
    </>
  );
}

/** A governed-setting bucket (platform, a country, a state, a market) in words: the scope's name, never its id. */
function useBucketName(): (scope: string, ref: string | undefined) => string {
  const scopeName = useScopeName();
  const { v } = useVocab();
  return (scope, ref) => {
    if (scope === 'platform' || scope === 'country' || scope === 'region' || scope === 'market') return scopeName({ scopeType: scope === 'platform' ? 'GLOBAL' : scope.toUpperCase(), ...(ref !== undefined && ref !== '' ? { scopeId: ref } : {}) });
    const kind = v('policy.scope', scope);
    return ref === undefined || ref === '' ? kind : `${kind} · ${scope === 'category' ? v('ride.category', ref) : ref}`;
  };
}

export function GovernedSettingsScreen(props: GovernedSettingsScreenProps = {}): ReactElement {
  const { only, heading, renderAbove, hidePolicies, showKeys = true } = props;
  const { runtime, scope, markets } = useAdmin();
  const { confirm, toast } = useConsole();
  const { t, lang } = useT();
  const { fail, v, has, explain } = useVocab();
  const bucketName = useBucketName();

  const [summaries, setSummaries] = useState<readonly GovernedSettingSummaryView[] | undefined>(undefined);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<GovernedSettingKeyResponse | undefined>(undefined);
  const [effective, setEffective] = useState<GovernedSettingEffectiveResponse | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<RuleFilter>('all');
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [draft, setDraft] = useState<unknown>(undefined);
  const [jsonText, setJsonText] = useState('');
  const [reason, setReason] = useState('');
  const [scheduleLocal, setScheduleLocal] = useState('');
  const [acknowledge, setAcknowledge] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // ---- the place and the level ----
  // The place follows the top scope selector; opening a value held somewhere else (or an area's "open at") moves it until the selector moves.
  // D768: another screen may open this one at a level (`?key=ai.monthlyBudget&level=country|PT`, Gastos de IA's "Alterar orçamento").
  const linked = useMemo(() => (typeof window === 'undefined' ? undefined : levelFromQuery(parseQuery(window.location.search).level)), []);
  const [placeOverride, setPlaceOverride] = useState<OperatorScope | undefined>(() => (linked === undefined ? undefined : placeOfLevel(linked)));
  const [levelChoice, setLevelChoice] = useState<string | undefined>(() => (linked === undefined ? undefined : levelId(linked)));
  const selectorKey = `${scope.current.country ?? ''}|${scope.current.region ?? ''}|${scope.current.city ?? ''}`;
  const shownSelector = useRef(selectorKey);
  useEffect(() => {
    if (shownSelector.current === selectorKey) return;
    shownSelector.current = selectorKey;
    setPlaceOverride(undefined);
    setLevelChoice(undefined);
  }, [selectorKey]);
  const place = placeOverride ?? scope.current;
  const levels = useMemo(() => levelsOfPlace(place, markets), [place, markets]);
  // The page opens on the most specific level of the place (the city, the state, the country or the platform).
  const chosen = levelChoice === undefined ? -1 : levels.findIndex((l) => levelId(l) === levelChoice);
  const selected = chosen >= 0 ? chosen : levels.length - 1;
  const level = levels[selected] as RuleLevel;
  const levelName = bucketName(level.scope, level.scopeRef);

  const refuse = useCallback((e: { reasonCode?: string; code: string }): void => {
    toast({ message: explain(e) ?? t('govset.err', { code: fail(e) }), tone: 'warning' });
  }, [t, toast, explain, fail]);

  const loadList = useCallback(async (): Promise<readonly GovernedSettingSummaryView[] | undefined> => {
    const res = await runtime.settings.list();
    if (!res.ok) {
      setError(t('govset.err', { code: fail(res.error) }));
      return undefined;
    }
    setError(undefined);
    const rows = only === undefined ? res.value.settings : res.value.settings.filter((s) => only.includes(s.key));
    setSummaries(rows);
    return rows;
  }, [runtime, t, only]);

  const loadKey = useCallback(async (key: string): Promise<void> => {
    const res = await runtime.settings.get(key);
    if (!res.ok) {
      setError(t('govset.err', { code: fail(res.error) }));
      return;
    }
    setError(undefined);
    setDetail(res.value);
  }, [runtime, t]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const list = await loadList();
      if (!alive) return;
      // D674: another screen may open this one on a key (`?key=geo.places`, the places screen's "Prazo de guarda"); an unknown key falls back to the first.
      const wanted = typeof window === 'undefined' ? undefined : parseQuery(window.location.search).key;
      const first = list?.find((s) => s.key === wanted)?.key ?? list?.[0]?.key;
      if (first !== undefined) {
        setSelectedKey(first);
        await loadKey(first);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [loadList, loadKey]);

  // The server's own fold at the selected level (STATE-SCOPE F1): it decides which level is in force and why a narrower one was held. A failed read
  // leaves the list's fold in its place: it explains, it never gates.
  const settingKey = detail?.setting.key;
  const stamp = detail?.at;
  useEffect(() => {
    if (settingKey === undefined) return undefined;
    let alive = true;
    setEffective(undefined);
    void runtime.settings.effective(settingKey, effectiveQueryOf(level)).then((res) => {
      if (alive && res.ok) setEffective(res.value);
    });
    return () => { alive = false; };
  }, [runtime, settingKey, level.scope, level.scopeRef, stamp]);

  const allowed = detail?.setting.allowedScopes.includes(level.scope) ?? false;
  // The bucket the editor proposes into: the one the server holds at the level, or an empty one (a first value is proposed from here).
  const bucket = useMemo(
    () => (detail === undefined ? undefined : bucketForLevel(detail.buckets, level, detail.setting.defaultValue, markets)),
    [detail, level.scope, level.scopeRef, markets],
  );
  const editor = settingEditorFor(detail?.setting.key ?? '');
  // D775: the shape of the value the server sends with the definition (absent for a key with no form yet).
  const valueForm = editor.kind === 'json' && detail?.setting.form !== undefined ? (detail.setting.form as SettingField) : undefined;
  const now = detail?.at ?? new Date().toISOString();
  // The currency the minor-unit amounts of the value are written in: the one a version carries, else the page's country pack.
  const detailForm = detail?.setting.form as SettingField | undefined;
  const currency = useMoneyCurrency(detailForm, [...(detail?.versions ?? []).map((ver) => ver.value), ...(detail?.buckets ?? []).map((b) => b.current?.value)], bucket, now);
  const valueView = useSettingValueView(detail?.setting.key, detailForm, currency);

  // What is in force at the level: its own version, else the one inherited from a level above (the list's fold, so the draft does not reset when
  // the server's fold arrives). A version no reader can verify is never a starting point (review W6G-04).
  const inForce = useMemo(() => {
    if (detail === undefined) return undefined;
    const src = ruleSource({ allowedScopes: detail.setting.allowedScopes, buckets: detail.buckets }, levels, selected);
    const current = src.kind === 'none' ? undefined : bucketAt(detail.buckets, src.level)?.current;
    return current === undefined || current.origin === 'UNVERIFIED' ? undefined : { value: current.value };
  }, [detail, levels, selected]);
  const formEditor = editor.kind === 'assistant' || editor.kind === 'refundCaps' || editor.kind === 'orderPayments' || editor.kind === 'aiModels' || editor.kind === 'speechEngine' || editor.kind === 'aiBudget' || editor.kind === 'autonomy';

  // The draft starts from what is in force at the level — its own value or the inherited one, as in the design — whenever the key or the level
  // changes (a form of "Atendimento e IA" keeps its own start); the reason starts empty.
  useEffect(() => {
    if (detail === undefined) return;
    const start = bucket?.current === undefined && inForce !== undefined && !formEditor ? inForce.value : initialEditorValue(detail.setting, bucket);
    setDraft(start);
    setJsonText(JSON.stringify(start, null, 2) ?? '');
    setScheduleLocal('');
    setAcknowledge(false);
    setReason('');
  }, [detail, bucket, inForce, formEditor]);

  const diff: readonly SettingDiffRow[] = useMemo(
    () => settingValueDiff(inForce?.value ?? detail?.setting.defaultValue ?? null, draft ?? null),
    [inForce, detail, draft],
  );
  const changes = settingDiffChanges(diff);

  const schedule = scheduleLocal.trim() === '' ? undefined : scheduleToUtc(scheduleLocal, bucket?.timezone, now);
  const scheduleIssue = schedule !== undefined && !schedule.ok ? schedule.issue : undefined;
  const effectiveFrom = schedule?.ok === true ? schedule.instant : undefined;
  const collisions = scheduledCollisions(bucket, effectiveFrom, now);
  const collisionKey = collisions.join(',');
  const named = collisions.map((n) => `v${String(n)}`).join(', ');
  // A tick names ONE set of versions: when the schedule moves and the set changes, the operator ticks again.
  useEffect(() => { setAcknowledge(false); }, [collisionKey]);
  /** The waiting versions a queued change would cancel if approved now (review W6G-05). */
  const cancelledBy = (change: SettingChangeView): readonly number[] =>
    change.status !== 'PENDING_REVIEW' ? [] : scheduledCollisions(detail?.buckets.find((b) => b.scope === change.scope && b.scopeRef === change.scopeRef), change.effectiveFrom, now);

  const selectKey = async (key: string): Promise<void> => {
    setSelectedKey(key);
    setDetail(undefined);
    await loadKey(key);
  };

  const reload = async (): Promise<void> => {
    await loadList();
    if (selectedKey !== undefined) await loadKey(selectedKey);
  };

  /** Move the page to a level of another place (a value held elsewhere, an area's "open at"). */
  const openPlace = (at: RuleLevel): void => {
    setPlaceOverride(placeOfLevel(at));
    setLevelChoice(levelId(at));
  };

  /** A mandatory free-text reason for a decision — what lands on the append-only trail. */
  const askReason = async (spec: { title: string; body: string; confirmLabel: string; tone: 'primary' | 'danger' }): Promise<string | undefined> => {
    const r = await confirm({
      title: spec.title, body: spec.body, confirmLabel: spec.confirmLabel, cancelLabel: t('ui.cancel'), tone: spec.tone,
      reasonLabel: t('govset.reasonLabel'), notePlaceholder: t('govset.reason.ph'), requireNote: true,
    });
    if (!r.confirmed) return undefined;
    const text = (r.note ?? r.reason ?? '').trim();
    return text.length > 0 ? text : undefined;
  };

  const block = submitBlock({
    allowed,
    hasDraft: draft !== undefined && !(editor.kind === 'tokens' && !editor.allowEmpty && Array.isArray(draft) && draft.length === 0)
      && (valueForm === undefined || settingFormIssues(valueForm, draft).length === 0),
    changes,
    reason,
    scheduleIssue: scheduleIssue !== undefined,
    collisionUnacknowledged: collisions.length > 0 && !acknowledge,
  });

  const submit = async (): Promise<void> => {
    if (busy || detail === undefined || bucket === undefined || block !== 'ready') return;
    setBusy(true);
    const res = await runtime.settings.propose(detail.setting.key, {
      scope: level.scope, ...(level.scopeRef !== '' ? { scopeRef: level.scopeRef } : {}), value: draft, reason: reason.trim(),
      ...(effectiveFrom !== undefined ? { effectiveFrom } : {}),
      ...(collisions.length > 0 && acknowledge ? { supersedesScheduledVersions: collisions } : {}),
    });
    setBusy(false);
    if (!res.ok) return refuse(res.error);
    toast({ message: t('govset.toast.proposed'), tone: 'success' });
    await reload();
  };

  const decide = async (change: SettingChangeView, action: 'approve' | 'reject' | 'withdraw' | 'review'): Promise<void> => {
    if (busy || detail === undefined) return;
    // Review W6G-05: the checker's acknowledgement is the checker's own — a confirmation that NAMES what is cancelled.
    const waiting = action === 'approve' ? cancelledBy(change) : [];
    const waitingNamed = waiting.map((n) => `v${String(n)}`).join(', ');
    const spec = {
      approve: change.kind === 'END'
        ? { title: t('govset.confirm.approveEnd.title'), body: t('govset.confirm.approveEnd.body'), confirmLabel: t('govset.queue.approveEnd'), tone: 'danger' as const }
        : waiting.length > 0
          ? { title: t('govset.confirm.approveCancels.title'), body: t('govset.confirm.approveCancels.body', { n: waitingNamed }), confirmLabel: t('govset.queue.approvePublish'), tone: 'danger' as const }
          : { title: t('govset.confirm.approve.title'), body: t('govset.confirm.approve.body'), confirmLabel: t('govset.queue.approvePublish'), tone: 'primary' as const },
      reject: { title: t('govset.confirm.reject.title'), body: t('govset.confirm.reject.body'), confirmLabel: t('govset.queue.reject'), tone: 'danger' as const },
      withdraw: { title: t('govset.confirm.withdraw.title'), body: t('govset.confirm.withdraw.body'), confirmLabel: t('govset.queue.withdraw'), tone: 'danger' as const },
      review: { title: t('govset.confirm.review.title'), body: t('govset.confirm.review.body'), confirmLabel: t('govset.queue.review'), tone: 'primary' as const },
    }[action];
    const text = await askReason(spec);
    if (text === undefined) return;
    const changeTarget = { scope: change.scope, ...(change.scopeRef !== '' ? { scopeRef: change.scopeRef } : {}) };
    // Sent only after the confirmation above named these versions to the checker (review W6S-01 / W6G-05).
    const input = { ...changeTarget, reason: text, ...(waiting.length > 0 ? { supersedesScheduledVersions: waiting } : {}) };
    setBusy(true);
    const res = action === 'approve'
      ? await runtime.settings.approve(detail.setting.key, change.id, input)
      : action === 'reject'
        ? await runtime.settings.reject(detail.setting.key, change.id, input)
        : action === 'withdraw'
          ? await runtime.settings.withdraw(detail.setting.key, change.id, input)
          : await runtime.settings.reviewRollback(detail.setting.key, change.id, input);
    setBusy(false);
    if (!res.ok) return refuse(res.error);
    const applied = res.value.change.appliedVersion;
    toast({
      message: action === 'approve' && applied !== undefined ? t('govset.toast.approved', { n: applied })
        : action === 'reject' ? t('govset.toast.rejected')
          : action === 'withdraw' ? t('govset.toast.withdrawn') : t('govset.toast.reviewed'),
      tone: 'success',
    });
    await reload();
  };

  const rollback = async (version: GovernedSettingVersionView): Promise<void> => {
    if (busy || detail === undefined || bucket === undefined) return;
    // W7O-03 (D414): a revert that is not the one-step shortcut — any older version, and EVERY revert of a key that
    // requires review (the OTP keys) — is a proposal a different operator approves; the dialog says so.
    const proposal = version.version !== oneStepRollbackTarget(bucket, detail.versions, detail.setting);
    const text = await askReason(proposal
      ? {
        title: t('govset.confirm.rollbackPropose.title', { n: version.version }), body: t('govset.confirm.rollbackPropose.body'),
        confirmLabel: t('govset.rollback.propose', { n: version.version }), tone: 'danger',
      }
      : {
        title: t('govset.confirm.rollback.title', { n: version.version }), body: t('govset.confirm.rollback.body'),
        confirmLabel: t('govset.rollback.action', { n: version.version }), tone: 'danger',
      });
    if (text === undefined) return;
    setBusy(true);
    const res = await runtime.settings.rollback(detail.setting.key, {
      scope: version.scope, ...(version.scopeRef !== '' ? { scopeRef: version.scopeRef } : {}),
      toVersion: version.version, toContentHash: version.contentHash, reason: text,
    });
    setBusy(false);
    if (!res.ok) return refuse(res.error);
    const applied = res.value.change.appliedVersion;
    toast({ message: applied !== undefined ? t('govset.toast.rolledBack', { n: applied }) : t('govset.toast.rollbackProposed'), tone: 'success' });
    await reload();
  };

  /**
   * D856 (LT-002 / L6b-018): "Voltar a herdar" - the level drops its own value and follows the level above again. A proposal
   * like any other: another person approves it before it applies.
   */
  const endLevel = async (): Promise<void> => {
    if (busy || detail === undefined || bucket === undefined) return;
    const text = await askReason({ title: t('govset.confirm.end.title'), body: t('govset.confirm.end.body'), confirmLabel: t('govset.end.action'), tone: 'danger' });
    if (text === undefined) return;
    setBusy(true);
    const res = await runtime.settings.end(detail.setting.key, { scope: level.scope, ...(level.scopeRef !== '' ? { scopeRef: level.scopeRef } : {}), reason: text });
    setBusy(false);
    if (!res.ok) return refuse(res.error);
    toast({ message: t('govset.toast.endProposed'), tone: 'success' });
    await reload();
  };

  /** An instant in the wall clock of the bucket it belongs to (a market's zone; the UTC fallback elsewhere). */
  const at = (instant: string | undefined, where?: { scope: string; scopeRef: string }): string =>
    formatMarketInstant(instant, (where === undefined ? bucket : detail?.buckets.find((b) => b.scope === where.scope && b.scopeRef === where.scopeRef))?.timezone, lang);

  // ---- the list ----
  const rows: readonly RuleRow[] = useMemo(() => (summaries ?? []).map((s) => {
    const catalogued = has('settings.key', s.key);
    return { key: s.key, name: catalogued ? v('settings.key', s.key) : s.key, catalogued, domain: ruleDomain(s.key, catalogued), source: ruleSource(s, levels, selected), pending: rulePendingCount(s) };
  }), [summaries, levels, selected, has, v]);
  const list = ruleList(rows, query, filter);
  const sourceLabel = (r: RuleRow): string => t(r.source.kind === 'own' ? 'govset.source.own' : r.source.kind === 'inherited' ? 'govset.source.inherited' : 'govset.source.none');

  const listPanel = (
    <RuleListPanel
      label={t('govset.keys.title')}
      head={(
        <>
          <RuleSearch label={t('govset.search.label')} placeholder={t('govset.search.ph')} value={query} onChange={setQuery} clearLabel={t('govset.search.clear')} />
          <SegmentedFilter
            label={t('govset.filter.label')}
            value={filter}
            onChange={(id) => setFilter(id as RuleFilter)}
            options={[
              { id: 'all', label: t('govset.filter.all'), count: list.counts.all },
              { id: 'pending', label: t('govset.filter.pending'), count: list.counts.pending },
              { id: 'empty', label: t('govset.filter.empty'), count: list.counts.empty },
            ]}
          />
        </>
      )}
      foot={hidePolicies === true ? undefined : (
        <RuleDisclosure label={t('govset.viewer.title')} open={policiesOpen} onToggle={() => setPoliciesOpen(!policiesOpen)}>
          <PoliciesViewer governedKeys={(summaries ?? []).map((s) => s.key)} />
        </RuleDisclosure>
      )}
    >
      {(summaries ?? []).length === 0 ? (
        <EmptyState title={t('govset.keys.empty')} />
      ) : list.total === 0 ? (
        <RuleListEmpty
          title={t('govset.list.none.title')}
          text={t('govset.list.none.text')}
          action={<Button size="sm" onClick={() => { setQuery(''); setFilter('all'); }}>{t('govset.list.none.reset')}</Button>}
        />
      ) : (
        list.groups.map((g) => (
          <RuleGroup key={g.domain} name={t(`govset.domain.${g.domain}`)} count={g.rows.length}>
            {g.rows.map((r) => (
              <RuleRowButton
                key={r.key}
                name={r.name}
                ruleKey={r.key}
                showKey={showKeys}
                catalogued={r.catalogued}
                source={r.source.kind}
                sourceLabel={sourceLabel(r)}
                {...(r.pending > 0 ? { pendingLabel: t('govset.row.pending', { n: r.pending }) } : {})}
                selected={r.key === selectedKey}
                onClick={() => void selectKey(r.key)}
              />
            ))}
          </RuleGroup>
        ))
      )}
    </RuleListPanel>
  );

  return (
    <>
      <PageHeader
        title={t(heading?.title ?? 'govset.title')}
        subtitle={t(heading?.subtitle ?? 'govset.lead')}
        actions={(
          <LevelSwitch
            label={t('govset.level.label')}
            value={levelId(level)}
            onChange={(id) => setLevelChoice(id)}
            options={levels.map((l) => ({ id: levelId(l), label: bucketName(l.scope, l.scopeRef), icon: LEVEL_ICON[l.scope] }))}
          />
        )}
      />
      {heading !== undefined && <Notice tone="neutral">{t(heading.explain)}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      {loading ? (
        <Panel><EmptyState title={t('govset.loading')} /></Panel>
      ) : (
        <Stack gap={5}>
          {renderAbove?.({ summaries: summaries ?? [], selectedKey, open: (key, s, ref) => { openPlace({ scope: s, scopeRef: ref }); void selectKey(key); } })}
          <ValueText.Provider value={valueView}>
          <RulesWorkspace
            list={listPanel}
            detail={(
              <RuleDetail label={t('govset.detail.label')}>
                {detail !== undefined && bucket !== undefined && (
                  <>
                    <RuleTitle
                      domain={t(`govset.domain.${ruleDomain(detail.setting.key, has('settings.key', detail.setting.key))}`)}
                      {...(showKeys ? { ruleKey: detail.setting.key } : {})}
                      name={has('settings.key', detail.setting.key) ? v('settings.key', detail.setting.key) : t('govset.unnamed')}
                      // D775: what the rule decides, and "Ler mais" for how it works with examples.
                      {...(has('settings.summary', detail.setting.key) ? {
                        description: (
                          <RuleDescription
                            key={detail.setting.key}
                            summary={v('settings.summary', detail.setting.key)}
                            {...(has('settings.details', detail.setting.key) ? { details: v('settings.details', detail.setting.key) } : {})}
                            moreLabel={t('govset.desc.more')}
                            lessLabel={t('govset.desc.less')}
                          />
                        ),
                      } : {})}
                      copyLabel={t('govset.copyKey')}
                      onCopy={() => {
                        try { void navigator.clipboard?.writeText(detail.setting.key); } catch { /* the toast still names the key */ }
                        toast({ message: t('govset.toast.copied', { key: detail.setting.key }), tone: 'success' });
                      }}
                    />

                    {detail.unverified.length > 0 && <Notice tone="danger">{t('govset.unverified.warn')}</Notice>}

                    <ReviewQueue
                      changes={detail.pending}
                      awaitingReview={detail.awaitingReview ?? []}
                      cancelledBy={cancelledBy}
                      at={at}
                      busy={busy}
                      onDecide={(c, a) => void decide(c, a)}
                    />

                    <RuleColumns>
                      <RuleColumn>
                        <InForce detail={detail} levels={levels} selected={selected} effective={effective} at={at} />

                        <RuleCard title={t('govset.editor.title')} sub={t('govset.propose.sub')}>
                          {!allowed ? (
                            <Notice tone="neutral">{t('govset.propose.notAllowed', { place: levelName })}</Notice>
                          ) : (
                            <>
                              <LevelNote icon={LEVEL_ICON[level.scope]} aside={t('govset.propose.switch')}>
                                {t('govset.propose.at')} <b>{levelName}</b>
                                {/* LT-010: a city has no "— and in every…" tail, so the sentence ends here, before "To change it…". */}
                                {level.scope === 'platform' ? <>{' '}{t('govset.propose.note.platform')}</>
                                  : level.scope === 'country' ? <>{' '}{t('govset.propose.note.country', { place: levelName })}</>
                                    : level.scope === 'region' ? <>{' '}{t('govset.propose.note.region', { place: levelName })}</> : '.'}
                              </LevelNote>

                              {(() => {
                                const raw = (
                                  <ValueEditor
                                    editor={editor}
                                    value={draft}
                                    jsonText={jsonText}
                                    onValue={setDraft}
                                    onJsonText={(text) => {
                                      setJsonText(text);
                                      try {
                                        setDraft(JSON.parse(text) as unknown);
                                      } catch {
                                        setDraft(undefined);
                                      }
                                    }}
                                  />
                                );
                                // D631: the keys of "Atendimento e IA" each have their own form (the screen remounts it with the key, the level and the version in force).
                                if (editor.kind === 'assistant' || editor.kind === 'refundCaps' || editor.kind === 'orderPayments' || editor.kind === 'aiModels' || editor.kind === 'speechEngine' || editor.kind === 'aiBudget' || editor.kind === 'autonomy') {
                                  return (
                                    <CentralAiValueEditor
                                      key={`${detail.setting.key}|${bucket.scope}|${bucket.scopeRef}|${String(bucket.current?.version ?? 0)}`}
                                      kind={editor.kind}
                                      // The form starts from what governs the bucket (read now, not from the draft the effect above sets a render later).
                                      value={initialEditorValue(detail.setting, bucket)}
                                      bucket={bucket}
                                      now={now}
                                      onValue={(next) => {
                                        setDraft(next);
                                        setJsonText(JSON.stringify(next ?? null, null, 2));
                                      }}
                                    />
                                  );
                                }
                                // D775: a key with no typed editor of its own is edited through the form its definition carries (never a free JSON box).
                                if (editor.kind === 'json' && valueForm !== undefined) {
                                  const form = (
                                    <SettingFormEditor
                                      key={`${detail.setting.key}|${bucket.scope}|${bucket.scopeRef}`}
                                      settingKey={detail.setting.key}
                                      form={valueForm}
                                      value={draft}
                                      bucket={bucket}
                                      now={now}
                                      onValue={(next) => {
                                        setDraft(next);
                                        setJsonText(JSON.stringify(next ?? null, null, 2));
                                      }}
                                    />
                                  );
                                  // D855 (owner Q3): the final-fare revision gets a live example calculator under its form.
                                  if (detail.setting.key !== 'fare.finalRevision') return form;
                                  return (
                                    <>
                                      {form}
                                      <FinalFareExample value={draft} />
                                    </>
                                  );
                                }
                                // D438 (C-18): an amount in a currency is typed through the money input, never as raw JSON.
                                if (editor.kind !== 'money') return raw;
                                const onMoney = (next: unknown): void => {
                                  setDraft(next);
                                  setJsonText(JSON.stringify(next ?? null, null, 2));
                                };
                                return <MoneySettingEditor value={draft} bucket={bucket} defaultValue={detail.setting.defaultValue} now={now} onValue={onMoney} fallback={raw} />;
                              })()}

                              {changes && (
                                <div>
                                  <Text role="caption" tone="secondary" block>{t('govset.diff.title')}</Text>
                                  <DiffTable rows={diff} />
                                </div>
                              )}

                              <CapWidth size="sm">
                                <Field
                                  label={t('govset.editor.schedule')}
                                  htmlFor="govset-schedule"
                                  hint={bucket.timezone !== undefined ? t('govset.editor.schedule.hint', { tz: bucket.timezone }) : t('govset.editor.schedule.noZone')}
                                >
                                  <TextInput id="govset-schedule" type="datetime-local" value={scheduleLocal} disabled={bucket.timezone === undefined} onChange={(e) => setScheduleLocal(e.target.value)} />
                                </Field>
                                {scheduleIssue !== undefined && (
                                  <Notice tone="warning">
                                    {scheduleIssue === 'NOT_IN_FUTURE' ? t('govset.editor.schedule.past') : scheduleIssue === 'NO_TIMEZONE' ? t('govset.editor.schedule.noZone') : t('govset.editor.schedule.invalid')}
                                  </Notice>
                                )}
                              </CapWidth>

                              {collisions.length > 0 && (
                                <Stack gap={3}>
                                  <Notice tone="warning">
                                    <strong>{t('govset.collision.title')}</strong>
                                    {t('govset.collision.explain')}
                                    {(bucket.scheduledVersions ?? (bucket.scheduled !== undefined ? [bucket.scheduled] : []))
                                      .filter((w) => collisions.includes(w.version))
                                      .map((w) => <div key={w.version}>{t('govset.collision.item', { n: w.version, at: at(w.effectiveFrom) })}</div>)}
                                  </Notice>
                                  <CheckRow id="govset-ack" checked={acknowledge} onChange={setAcknowledge}>
                                    {t('govset.collision.ack', { n: named })}
                                  </CheckRow>
                                </Stack>
                              )}

                              <Field label={t('govset.propose.reason')} htmlFor="govset-reason">
                                <TextInput id="govset-reason" value={reason} placeholder={t('govset.propose.reason.ph')} onChange={(e) => setReason(e.target.value)} />
                              </Field>

                              <SubmitRow
                                action={<Button variant="primary" onClick={() => void submit()} disabled={busy || block !== 'ready'}>{t('govset.editor.submit')}</Button>}
                                hint={block === 'ready' ? t('govset.hint.ready', { place: levelName }) : t(`govset.hint.${block}`)}
                              />
                            </>
                          )}
                        </RuleCard>
                      </RuleColumn>

                      <RuleColumn>
                        <ValuePerLevel detail={detail} levels={levels} selected={selected} effective={effective} onLevel={(l) => setLevelChoice(levelId(l))} onPlace={openPlace} />
                        <History
                          detail={detail}
                          levels={levels}
                          bucket={bucket}
                          level={level}
                          effective={effective}
                          selected={selected}
                          at={at}
                          busy={busy}
                          onRollback={(ver) => void rollback(ver)}
                          onEnd={() => void endLevel()}
                        />
                      </RuleColumn>
                    </RuleColumns>
                  </>
                )}
              </RuleDetail>
            )}
          />
          </ValueText.Provider>
        </Stack>
      )}
    </>
  );
}

/** The level whose version governs the selected level: the server's fold when it answered, else the list's. */
function decidingLevel(detail: GovernedSettingKeyResponse, levels: readonly RuleLevel[], selected: number, effective: GovernedSettingEffectiveResponse | undefined): RuleLevel | undefined {
  if (effective !== undefined) {
    const r = effective.resolution;
    if (r.scope === undefined) return undefined;
    return levels.find((l) => isLevel(l, { scope: r.scope as string, scopeRef: r.scopeRef ?? '' })) ?? { scope: r.scope as RuleLevel['scope'], scopeRef: r.scopeRef ?? '' };
  }
  const local = ruleSource({ allowedScopes: detail.setting.allowedScopes, buckets: detail.buckets }, levels, selected);
  return local.kind === 'none' ? undefined : local.level;
}

/** "Em vigor em …": the value in force at the selected level, where it comes from, its version, since when and who approved it. */
function InForce({ detail, levels, selected, effective, at }: {
  detail: GovernedSettingKeyResponse;
  levels: readonly RuleLevel[];
  selected: number;
  effective: GovernedSettingEffectiveResponse | undefined;
  at: (i: string | undefined, where?: { scope: string; scopeRef: string }) => string;
}): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const bucketName = useBucketName();
  const level = levels[selected] as RuleLevel;
  const levelName = bucketName(level.scope, level.scopeRef);
  const decider = decidingLevel(detail, levels, selected, effective);
  const winner = decider === undefined ? undefined : bucketAt(detail.buckets, decider);
  const current = winner?.current;
  const inherited = decider !== undefined && !isLevel(level, decider);
  const failClosed = winner?.failClosed === true || current?.origin === 'UNVERIFIED' || effective?.resolution.failClosed === true;
  const reason = effective?.resolution.reason;
  const held = reason !== undefined && reason !== 'RESOLVED' && reason !== 'NOT_SET' ? reason : undefined;
  const approval = current === undefined ? undefined : detail.approvals.find((a) => a.scope === current.scope && a.scopeRef === current.scopeRef && a.version === current.version);
  const scheduled = winner?.scheduledVersions ?? (winner?.scheduled !== undefined ? [winner.scheduled] : []);
  const srcTone: RulePillTone = current === undefined ? 'warning' : inherited ? 'neutral' : 'ink';
  return (
    <RuleCard
      title={t('govset.eff.title', { place: levelName })}
      aside={(
        <>
          <RulePill tone={srcTone}>{current === undefined ? t('govset.filter.empty') : inherited && decider !== undefined ? t('govset.eff.inherited', { place: bucketName(decider.scope, decider.scopeRef) }) : t('govset.eff.own')}</RulePill>
          {current !== undefined && <Text role="caption" tone="tertiary" mono>{t('govset.version', { n: current.version })}</Text>}
        </>
      )}
    >
      {current === undefined || decider === undefined ? (
        <EmptyValue title={t('govset.eff.noneTitle')} text={t('govset.eff.noneText', { place: levelName })} />
      ) : failClosed ? (
        // Review W6G-04: what production readers fold here is a version no one can verify — the bucket fails closed.
        <Notice tone="danger">{t('govset.current.failClosed', { n: current.version })}</Notice>
      ) : (
        <>
          <ValueBody value={current.value} />
          <MetaLine>
            <span>{t('govset.eff.since')} <MonoText>{at(current.effectiveFrom, current)}</MonoText></span>
            {approval !== undefined
              ? <span>{t('govset.eff.approvedBy')} <b><PersonName id={approval.approvedBy} /></b></span>
              : <span>{v('settings.origin', current.origin)}</span>}
          </MetaLine>
        </>
      )}
      {held !== undefined && <Notice tone="warning">{t('govset.chain.held', { reason: v('policy.resolution_reason', held) })}</Notice>}
      {scheduled.length > 0 && (
        <Notice tone="warning">
          {scheduled.map((w) => <div key={w.version}>{t('govset.collision.item', { n: w.version, at: at(w.effectiveFrom, w) })}</div>)}
        </Notice>
      )}
    </RuleCard>
  );
}

const TAG_TONE: Readonly<Record<ChainTag, RulePillTone>> = {
  appliesHere: 'success', appliesInherited: 'success', overridden: 'neutral', ownValue: 'neutral', inherits: 'quiet', notSet: 'quiet', notAllowed: 'quiet',
};

/** "Valor por nível": the chain most specific first, the level that decides marked; a name moves the page to that level. */
function ValuePerLevel({ detail, levels, selected, effective, onLevel, onPlace }: {
  detail: GovernedSettingKeyResponse;
  levels: readonly RuleLevel[];
  selected: number;
  effective: GovernedSettingEffectiveResponse | undefined;
  onLevel: (level: RuleLevel) => void;
  onPlace: (level: RuleLevel) => void;
}): ReactElement {
  const { t } = useT();
  const bucketName = useBucketName();
  const winner = effective === undefined ? undefined : { ...(effective.resolution.scope !== undefined ? { scope: effective.resolution.scope } : {}), ...(effective.resolution.scopeRef !== undefined ? { scopeRef: effective.resolution.scopeRef } : {}) };
  const rows = chainRows({ allowedScopes: detail.setting.allowedScopes, buckets: detail.buckets }, levels, selected, winner);
  const elsewhere = otherPlaces(detail.buckets, levels);
  return (
    <RuleCard title={t('govset.levels.title')} sub={t('govset.levels.sub')} flush>
      <LevelChain>
        {rows.map((r, i) => {
          const name = bucketName(r.level.scope, r.level.scopeRef);
          const current = r.bucket?.current;
          return (
            <LevelChainItem
              key={levelId(r.level)}
              label={name}
              {...(r.here ? {} : { onOpen: () => onLevel(r.level) })}
              tag={r.bucket?.failClosed === true ? t('govset.chain.failClosed') : t(`govset.tag.${r.tag}`)}
              tagTone={r.bucket?.failClosed === true ? 'danger' : TAG_TONE[r.tag]}
              here={r.here}
              hereLabel={t('govset.levels.here')}
              {...(current !== undefined && current.origin !== 'UNVERIFIED' ? { value: <ValueSummary value={current.value} /> } : {})}
              decides={r.decides}
              held={current !== undefined}
              below={r.below}
              struck={r.tag === 'overridden'}
              last={i === rows.length - 1}
            />
          );
        })}
      </LevelChain>
      {elsewhere.length > 0 && (
        <RuleCardBody>
          <Text role="caption" tone="tertiary" block>{t('govset.levels.others')}</Text>
          <Cluster gap={2} wrap>
            {elsewhere.map((l) => (
              <Button key={levelId(l)} size="sm" variant="ghost" onClick={() => onPlace(l)}>{t('govset.levels.open', { place: bucketName(l.scope, l.scopeRef) })}</Button>
            ))}
          </Cluster>
        </RuleCardBody>
      )}
    </RuleCard>
  );
}

/** "Histórico": the versions of the levels on screen, newest first, with the revert of the selected level and the full trail on demand. */
function History({ detail, levels, bucket, level, effective, selected, at, busy, onRollback, onEnd }: {
  detail: GovernedSettingKeyResponse;
  levels: readonly RuleLevel[];
  bucket: GovernedSettingBucketView;
  level: RuleLevel;
  effective: GovernedSettingEffectiveResponse | undefined;
  selected: number;
  at: (i: string | undefined, where?: { scope: string; scopeRef: string }) => string;
  busy: boolean;
  onRollback: (v: GovernedSettingVersionView) => void;
  onEnd: () => void;
}): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const bucketName = useBucketName();
  const [trail, setTrail] = useState(false);
  const decider = decidingLevel(detail, levels, selected, effective);
  const rows = detail.versions
    .filter((ver) => levels.some((l) => isLevel(l, ver)))
    .sort((a, b) => Date.parse(b.effectiveFrom) - Date.parse(a.effectiveFrom) || b.version - a.version);
  const oneStep = oneStepRollbackTarget(bucket, detail.versions, detail.setting);
  const reviewFirst = detail.setting.rollbackRequiresReview === true;
  // D856: a level that holds a value of its own (not the platform: nothing above it) may return to inherit, unless a change waits.
  const canEnd = level.scope !== 'platform' && bucket.current !== undefined && bucket.pending.length === 0 && (bucket.scheduledVersions ?? []).length === 0;
  return (
    <RuleCard title={t('govset.history.title')} aside={<Badge tone="neutral">{String(rows.length)}</Badge>} flush>
      {rows.length === 0 ? (
        <RuleCardBody><Text role="caption" tone="tertiary">{t('govset.hist.empty')}</Text></RuleCardBody>
      ) : rows.map((ver, at0) => {
        // The version this one replaced at its own level (the next older one in the list): the fields that differ are the news.
        const before = rows.slice(at0 + 1).find((o) => o.scope === ver.scope && o.scopeRef === ver.scopeRef && o.origin !== 'UNVERIFIED');
        const approval = detail.approvals.find((a) => a.scope === ver.scope && a.scopeRef === ver.scopeRef && a.version === ver.version);
        const inForce = ver.status === 'ACTIVE' && decider !== undefined && isLevel(decider, ver);
        const revertable = isLevel(level, ver) && canRevertTo(bucket, ver);
        return (
          <HistoryEntry
            key={`${ver.scope}|${ver.scopeRef}|${ver.version}`}
            title={t('govset.version', { n: ver.version })}
            pills={(
              <>
                {inForce && <RulePill tone="success">{t('govset.hist.current')}</RulePill>}
                {ver.retired === true && <RulePill tone="neutral">{t('govset.hist.ended')}</RulePill>}
                {ver.status === 'SCHEDULED' && <RulePill tone="warning">{t('govset.hist.scheduled')}</RulePill>}
                {ver.origin === 'LEGACY' && <RulePill tone="warning">{t('govset.hist.legacy')}</RulePill>}
                {ver.origin === 'UNVERIFIED' && <RulePill tone="danger">{v('settings.origin', ver.origin)}</RulePill>}
              </>
            )}
            when={at(ver.effectiveFrom, ver)}
            value={ver.origin === 'UNVERIFIED' ? '—' : ver.retired === true ? t('govset.hist.endedValue') : <ValueChanges value={ver.value} before={before} />}
            by={<>{bucketName(ver.scope, ver.scopeRef)} · {approval !== undefined ? <PersonName id={approval.approvedBy} /> : v('settings.origin', ver.origin)}</>}
          >
            {revertable && (
              <Cluster gap={3} wrap>
                <Button size="sm" variant="secondary" onClick={() => onRollback(ver)} disabled={busy}>
                  {ver.version === oneStep ? t('govset.rollback.action', { n: ver.version }) : t('govset.rollback.propose', { n: ver.version })}
                </Button>
                <Text role="caption" tone="tertiary">
                  {reviewFirst ? t('govset.rollback.reviewFirst') : ver.version === oneStep ? t('govset.rollback.oneStep') : t('govset.rollback.fourEyes')}
                </Text>
              </Cluster>
            )}
          </HistoryEntry>
        );
      })}
      {canEnd && (
        <RuleCardBody>
          <Cluster gap={3} wrap>
            <Button size="sm" variant="secondary" onClick={onEnd} disabled={busy}>{t('govset.end.action')}</Button>
            <Text role="caption" tone="tertiary">{t('govset.end.hint')}</Text>
          </Cluster>
        </RuleCardBody>
      )}
      <RuleDisclosure label={trail ? t('govset.trail.hide') : t('govset.trail.show')} open={trail} onToggle={() => setTrail(!trail)}>
        <HistoryTimeline settingKey={detail.setting.key} scope={level.scope} scopeRef={level.scopeRef} at={(i) => at(i)} />
      </RuleDisclosure>
    </RuleCard>
  );
}

/** The rule's review queue: the proposals waiting for a decision and the applied rollbacks waiting for their post-review (review W6G-01). */
function ReviewQueue({ changes, awaitingReview, cancelledBy, at, busy, onDecide }: {
  changes: readonly SettingChangeView[];
  awaitingReview: readonly SettingChangeView[];
  /** Review W6G-05: the scheduled versions approving a queued change now would cancel. */
  cancelledBy: (change: SettingChangeView) => readonly number[];
  at: (i: string | undefined, where?: { scope: string; scopeRef: string }) => string;
  busy: boolean;
  onDecide: (change: SettingChangeView, action: 'approve' | 'reject' | 'withdraw' | 'review') => void;
}): ReactElement | null {
  const { t } = useT();
  const valueText = useContext(ValueText).text;
  const bucketName = useBucketName();
  const total = changes.length + awaitingReview.length;
  if (total === 0) return null;
  const meta = (c: SettingChangeView): ReactNode => (
    <>
      <span><b><PersonName id={c.requestedBy} /></b> {t('govset.pend.proposed', { at: at(c.requestedAt, c) })}</span>
      <span>{t('govset.pend.applyAt')} <b>{bucketName(c.scope, c.scopeRef)}</b></span>
    </>
  );
  return (
    <RuleCard tone="warning" title={total === 1 ? t('govset.pend.one') : t('govset.pend.many', { n: total })} sub={t('govset.queue.explain')} flush>
      {awaitingReview.map((c) => (
        <PendingProposal
          key={c.id}
          meta={meta(c)}
          value={<ValueProposed from={c.fromValue} to={c.toValue} hasFrom={'fromValue' in c} />}
          reason={`“${c.requestReason}”`}
          notice={<Notice tone="warning">{t('govset.queue.reviewPending')}</Notice>}
          actions={<Button size="sm" onClick={() => onDecide(c, 'review')} disabled={busy}>{t('govset.queue.review')}</Button>}
        />
      ))}
      {changes.map((c) => {
        const cancels = cancelledBy(c);
        return (
          <PendingProposal
            key={c.id}
            meta={meta(c)}
            value={c.kind === 'END' ? t('govset.pend.endValue', { value: valueText(c.fromValue) }) : <ValueProposed from={c.fromValue} to={c.toValue} hasFrom={c.fromValue !== undefined} />}
            reason={`“${c.requestReason}”`}
            notice={(
              <>
                {c.effectiveFrom !== undefined && <Text role="caption" tone="secondary">{t('govset.queue.scheduledFor')} {at(c.effectiveFrom, c)}</Text>}
                {cancels.length > 0 && <Notice tone="warning">{t('govset.queue.cancels', { n: cancels.map((n) => `v${String(n)}`).join(', ') })}</Notice>}
              </>
            )}
            actions={(
              <>
                <Button size="sm" variant="ghost" onClick={() => onDecide(c, 'withdraw')} disabled={busy}>{t('govset.queue.withdraw')}</Button>
                <Button size="sm" variant="secondary" onClick={() => onDecide(c, 'reject')} disabled={busy}>{t('govset.queue.reject')}</Button>
                <Button size="sm" icon="check" onClick={() => onDecide(c, 'approve')} disabled={busy}>{t('govset.queue.approvePublish')}</Button>
              </>
            )}
          />
        );
      })}
    </RuleCard>
  );
}

function DiffTable({ rows }: { rows: readonly SettingDiffRow[] }): ReactElement {
  const { t } = useT();
  const TONE: Readonly<Record<SettingDiffRow['kind'], 'neutral' | 'success' | 'warning' | 'danger'>> = {
    added: 'success', removed: 'danger', changed: 'warning', unchanged: 'neutral',
  };
  if (rows.length === 0 || rows.every((r) => r.kind === 'unchanged')) return <Notice tone="neutral">{t('govset.diff.none')}</Notice>;
  return (
    <Stack>
      {rows.map((r) => (
        <HairlineRow key={r.path} align="baseline">
          <Text role="caption" tone="secondary">{r.path === '' ? t('govset.diff.field') : r.path}</Text>
          <Badge tone={TONE[r.kind]}>{t(`govset.diff.kind.${r.kind}`)}</Badge>
          <Text role="caption" tone="tertiary" mono>{r.kind === 'changed' || r.kind === 'removed' ? <del>{r.from ?? '—'}</del> : (r.from ?? '—')}</Text>
          <Text role="label" mono>{r.to ?? '—'}</Text>
        </HairlineRow>
      ))}
    </Stack>
  );
}

function ValueEditor({ editor, value, jsonText, onValue, onJsonText }: {
  editor: ReturnType<typeof settingEditorFor>;
  value: unknown;
  jsonText: string;
  onValue: (v: unknown) => void;
  onJsonText: (text: string) => void;
}): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const [newToken, setNewToken] = useState('');

  if (editor.kind === 'choice') {
    return (
      <CapWidth size="sm">
        <Field label={t('govset.editor.value')} htmlFor="govset-choice">
          <Select id="govset-choice" value={typeof value === 'string' ? value : ''} onChange={(e) => onValue(e.target.value)}>
            {editor.options.map((o) => <option key={o} value={o}>{v('dispatch_strategies.dispatch_mode', o)}</option>)}
          </Select>
        </Field>
      </CapWidth>
    );
  }

  if (editor.kind === 'tokens' || editor.kind === 'duration') {
    const tokens = editor.kind === 'tokens'
      ? (Array.isArray(value) ? (value as string[]) : [])
      : (typeof value === 'object' && value !== null && Array.isArray((value as { rideOrigins?: unknown }).rideOrigins) ? ((value as { rideOrigins: string[] }).rideOrigins) : []);
    const suggested = editor.kind === 'tokens' ? editor.suggested : editor.suggestedOrigins;
    const setTokens = (next: readonly string[]): void => {
      const sorted = [...new Set(next)].sort();
      if (editor.kind === 'tokens') onValue(sorted);
      else onValue({ timeoutSeconds: (value as { timeoutSeconds?: number } | undefined)?.timeoutSeconds ?? editor.minSeconds, rideOrigins: sorted });
    };
    const label = editor.kind === 'duration' ? t('govset.editor.origins') : t('govset.editor.value');
    return (
      <Stack gap={4}>
        {editor.kind === 'duration' && (
          <CapWidth size="xs">
            <Field label={t('govset.editor.seconds')} htmlFor="govset-seconds" hint={t('govset.editor.seconds.hint', { min: editor.minSeconds, max: editor.maxSeconds })}>
              <TextInput
                id="govset-seconds"
                type="number"
                inputMode="numeric"
                min={editor.minSeconds}
                max={editor.maxSeconds}
                value={String((value as { timeoutSeconds?: number } | undefined)?.timeoutSeconds ?? '')}
                onChange={(e) => onValue({ timeoutSeconds: Number(e.target.value), rideOrigins: tokens })}
              />
            </Field>
          </CapWidth>
        )}
        <Stack gap={2}>
          <Text role="caption" tone="secondary" block>{label}</Text>
          {/* Review W6G-09 (D414): a selected token is a pressed chip, not a primary button. */}
          <OptionChips label={label}>
            {[...new Set([...suggested, ...tokens])].sort().map((token) => {
              const on = tokens.includes(token);
              return <OptionChip key={token} on={on} onClick={() => setTokens(on ? tokens.filter((x) => x !== token) : [...tokens, token])}>{token}</OptionChip>;
            })}
          </OptionChips>
          <FormRow
            columns="minmax(0, 360px)"
            action={
              <Button variant="secondary" disabled={newToken.trim() === ''} onClick={() => { setTokens([...tokens, newToken.trim()]); setNewToken(''); }}>
                {t('govset.editor.tokens.add')}
              </Button>
            }
          >
            <Field label={t('govset.editor.tokens.new')} htmlFor="govset-token">
              <TextInput id="govset-token" value={newToken} onChange={(e) => setNewToken(e.target.value)} />
            </Field>
          </FormRow>
        </Stack>
      </Stack>
    );
  }

  // D616 round 2: one bounded integer percentage inside `{ confirmPercent }`.
  if (editor.kind === 'percent') {
    const current = (value as Record<string, unknown> | null | undefined)?.[editor.field];
    return (
      <CapWidth size="xs">
        <Field label={t('govset.editor.percent')} htmlFor="govset-percent" hint={t('govset.editor.percent.hint', { min: editor.min, max: editor.max })}>
          <TextInput
            id="govset-percent"
            type="number"
            inputMode="numeric"
            min={editor.min}
            max={editor.max}
            value={typeof current === 'number' ? String(current) : ''}
            onChange={(e) => onValue({ [editor.field]: e.target.value === '' ? undefined : Number(e.target.value) })}
          />
        </Field>
      </CapWidth>
    );
  }

  // W7-GOVERNED-OTP-KEYS (D410): the country calling code — digits only, the server decides (and refuses a code another
  // country governs, `CALLING_CODE_ALREADY_GOVERNED`).
  if (editor.kind === 'callingCode') {
    const text = typeof value === 'string' ? value : '';
    const draft = callingCodeDraft(text);
    return (
      <CapWidth size="xs">
        <Field label={t('govset.editor.callingCode')} htmlFor="govset-calling-code" hint={text !== '' && !draft.ok ? t('govset.editor.callingCode.invalid', { max: editor.maxDigits }) : t('govset.editor.callingCode.hint')}>
          <TextInput
            id="govset-calling-code"
            inputMode="numeric"
            autoComplete="off"
            maxLength={editor.maxDigits}
            value={text}
            onChange={(e) => onValue(e.target.value.replace(/\s+/g, ''))}
          />
        </Field>
      </CapWidth>
    );
  }

  // D410: the OTP anti-abuse thresholds as the D381 form — one bounded number per field, the phone's last-token
  // challenge switch, and the challenge kind. Every edit keeps the other fields.
  if (editor.kind === 'otpAbuse') {
    const last = otpAbuseFieldValue(value, 'phone.challengeOnLastToken');
    const kind = otpAbuseFieldValue(value, 'challenge.kind');
    return (
      <Stack gap={4}>
        <Grid min="sm" gap={3}>
          {editor.fields.map((f) => {
            const id = `govset-otp-${f.path.replace('.', '-')}`;
            const current = otpAbuseFieldValue(value, f.path);
            return (
              <Field key={f.path} label={t(`govset.editor.otp.${f.path}`)} htmlFor={id} hint={t('govset.editor.otp.bounds', { min: f.min, max: f.max })}>
                <TextInput
                  id={id}
                  type="number"
                  inputMode="numeric"
                  min={f.min}
                  max={f.max}
                  value={typeof current === 'number' ? String(current) : ''}
                  onChange={(e) => onValue(withOtpAbuseField(value, f.path, e.target.value === '' ? undefined : Number(e.target.value)))}
                />
              </Field>
            );
          })}
        </Grid>
        <CheckRow id="govset-otp-last-token" checked={last === true} onChange={(on) => onValue(withOtpAbuseField(value, 'phone.challengeOnLastToken', on))}>
          {t('govset.editor.otp.challengeOnLastToken')}
        </CheckRow>
        <CapWidth size="sm">
          <Field label={t('govset.editor.otp.challenge.kind')} htmlFor="govset-otp-kind">
            <Select id="govset-otp-kind" value={typeof kind === 'string' ? kind : ''} onChange={(e) => onValue(withOtpAbuseField(value, 'challenge.kind', e.target.value))}>
              <option value="" disabled>{t('govset.editor.otp.kind.none')}</option>
              {editor.challengeKinds.map((k) => <option key={k} value={k}>{v('identity.otp_challenge_kind', k)}</option>)}
            </Select>
          </Field>
        </CapWidth>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {/* D775: only a key the form registry does not know yet reaches this box; the server still validates at preview, proposal and approval. */}
      <Notice tone="warning">{t('govset.form.noForm')}</Notice>
      <Field label={t('govset.editor.json')} htmlFor="govset-json" hint={value === undefined ? t('govset.editor.json.invalid') : undefined}>
        <TextArea id="govset-json" rows={6} value={jsonText} onChange={(e) => onJsonText(e.target.value)} />
      </Field>
    </Stack>
  );
}

/** The append-only trail of one bucket: every revision, with who decided it and why. */
function HistoryTimeline({ settingKey, scope, scopeRef, at }: { settingKey: string; scope: string; scopeRef: string; at: (i: string | undefined) => string }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const [rows, setRows] = useState<readonly SettingChangeView[]>([]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await runtime.settings.history(settingKey, { scope: scope as 'platform', ...(scopeRef !== '' ? { scopeRef } : {}) });
      if (alive && res.ok) setRows(res.value.changes);
    })();
    return () => { alive = false; };
  }, [runtime, settingKey, scope, scopeRef]);

  // Review W6G-01: the post-review of an applied rollback is actionable in the queue above. This trail lists EVERY
  // revision, so a rollback's APPLIED revision stays here after its review — it must not raise a warning of its own.
  return rows.length === 0 ? (
    <Text role="caption" tone="tertiary">{t('govset.history.empty')}</Text>
  ) : (
    <Stack>
      {[...rows].reverse().map((c) => (
        <HairlineRow key={`${c.id}-${c.revision}`} align="baseline">
          <Badge tone={lifecycleTone(c.lifecycle)}>{v('platform.decision_reason', c.reasonCode)}</Badge>
          <Text role="caption" tone="secondary"><PersonName id={c.resolvedBy ?? c.requestedBy} /></Text>
          <SelfApprovalNote record={c} />
          <HairlineRowMain><Text role="caption">{c.resolutionReason ?? c.requestReason}</Text></HairlineRowMain>
          <Text role="caption" tone="tertiary" mono>{at(c.resolvedAt ?? c.requestedAt)}</Text>
        </HairlineRow>
      ))}
    </Stack>
  );
}

/**
 * Admin checklist #59 — the READ-ONLY viewer of the generic platform policies (`GET /v1/config/policies/:key` and
 * `/resolve`). These keys do not go through the four-eyes review above, so nothing here writes: an operator reads the
 * published versions and simulates how the key resolves at a scope. A key that IS governed above is sent back up
 * rather than read through the generic route.
 */
function PoliciesViewer({ governedKeys }: { governedKeys: readonly string[] }): ReactElement {
  const { runtime } = useAdmin();
  const { t, lang } = useT();
  const bucketName = useBucketName();
  const { fail, v } = useVocab();
  const [key, setKey] = useState('');
  const [versions, setVersions] = useState<readonly PolicySettingView[] | undefined>(undefined);
  const [issue, setIssue] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const trimmed = key.trim();
  const governed = governedKeys.includes(trimmed);

  const load = async (): Promise<void> => {
    if (busy || trimmed === '' || governed) return;
    setBusy(true);
    const res = await runtime.config.policyHistory(trimmed);
    setBusy(false);
    if (!res.ok) {
      setVersions(undefined);
      setIssue(t('govset.err', { code: fail(res.error) }));
      return;
    }
    setIssue(undefined);
    setVersions(res.value.versions);
  };

  return (
    <Stack gap={3}>
      <Text as="p" role="caption" tone="tertiary">{t('govset.viewer.explain')}</Text>
      <FormRow action={<Button variant="secondary" onClick={() => void load()} disabled={busy || trimmed === '' || governed}>{t('govset.viewer.load')}</Button>}>
        <Field label={t('govset.viewer.key')} htmlFor="govset-viewer-key">
          <TextInput id="govset-viewer-key" value={key} placeholder={t('govset.viewer.key.ph')} onChange={(e) => setKey(e.target.value)} />
        </Field>
      </FormRow>
      {governed && <Notice tone="neutral">{t('govset.viewer.governed')}</Notice>}
      {issue !== undefined && <Notice tone="danger">{issue}</Notice>}
      {versions !== undefined && (versions.length === 0 ? (
        <EmptyState title={t('govset.viewer.empty')} />
      ) : (
        [...versions].sort((a, b) => b.version - a.version).map((ver) => (
          <HairlineRow key={`${ver.scope}|${ver.scopeRef ?? ''}|${ver.version}`} align="baseline">
            <Text role="label">{t('govset.version', { n: ver.version })}</Text>
            <Badge tone="neutral">{bucketName(ver.scope, ver.scopeRef)}</Badge>
            <Badge tone="neutral">{v('policy.override_mode', ver.mode)}</Badge>
            <HairlineRowMain><Text role="caption" tone="secondary" mono>{renderValue(ver.value)}</Text></HairlineRowMain>
            <Text role="caption" tone="tertiary" mono>{formatMarketInstant(ver.effectiveFrom, undefined, lang)}</Text>
          </HairlineRow>
        ))
      ))}
    </Stack>
  );
}
